<template>
  <div class="form">
    <div class="form__content">
      <IdentityQueueStepCounter />
      <h1 class="extra-large-headline">
        {{ $t("PageTitle.headline.SofDocumentUploadPageHeader") }}
      </h1>
      <p class="bodycopy">
        {{ $t("PageSubTitle.paragraph.sofDocumentUploadInstructions") }}
      </p>
      <ul class="form__document-instructions bodycopy">
        <li v-for="list in bodyCopy" :key="list">
          {{ list }}
        </li>
      </ul>
    </div>
    <form @submit.prevent>
      <FormAlert :text="$t('FormAlert.text.documentUploadInfo')" />

      <MultiFileUploadField
        name="sofDocumentUpload"
        iconType="DocumentCircle"
        :errors="form.sofDocumentUploadErrors"
        :label="$t('MultiFileUploadField.label.genericUploadLabel')"
        :placeholder="$t('FileUploadField.placeholder.selectOrDrag')"
        @updateFiles="updateFiles"
      />

      <Checkbox
        name="tnc"
        :errors="form.tncErrors"
        :text="$t('Checkbox.text.sofDocumentUploadTnc')"
        v-model:value="form.tnc"
        v-model:tnc="form.tnc"
      />

      <ButtonBase
        :isDisabled="formHasErrors || globalStore.getIsFormLoading"
        :isLoading="globalStore.getIsFormLoading"
        @onButtonClick="handleFormSubmit"
      >
        {{ $t("ButtonBase.label.sofSelfie") }}
      </ButtonBase>
      <IdentityQueueNext />
    </form>
  </div>
</template>

<script setup>
import { ref, watch, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";

import { useGlobalStore } from "@/stores/globalStore";
import { useIdentityQueueStore } from "@/stores/identityQueueStore";

import useCustomerActions from "@/composables/api/useCustomerActions";
import useApiHelpers from "@/composables/api/helpers";

import MultiFileUploadField from "@/components/ui/inputs/MultiFileUploadField.vue";
import Checkbox from "@/components/ui/inputs/Checkbox.vue";
import ButtonBase from "@/components/ui/buttons/ButtonBase.vue";
import useFormValidation from "@/composables/validations/useFormValidation";
import FormAlert from "@/components/ui/alerts/FormAlert.vue";
import IdentityQueueStepCounter from "@/components/layout/progress/IdentityQueueStepCounter.vue";
import IdentityQueueNext from "@/components/layout/progress/IdentityQueueNext.vue";

const route = useRoute();
const router = useRouter();

const globalStore = useGlobalStore();
const identityQueueStore = useIdentityQueueStore();
const { validateMultiFileInput, validateCheckboxField } = useFormValidation();
const { fetchCustomerId } = useCustomerActions();
const { postRequest } = useApiHelpers();
const { t } = useI18n();
const form = ref({
  sofDocumentUpload: [],
  sofDocumentUploadErrors: "",
  tnc: false,
  tncErrors: "",
});
const updateFiles = (files) => {
  form.value.sofDocumentUpload = [];
  form.value.sofDocumentUpload = [...files];

  const fileValidationErrors = validateMultiFileInput(
    "sofDocumentUpload",
    form.value.sofDocumentUpload
  );

  form.value.sofDocumentUploadErrors = fileValidationErrors;
};

const formHasErrors = computed(() => {
  const anyErrors =
    form.value.sofDocumentUploadErrors !== "" || form.value.tncErrors !== "";
  const anyFieldsEmpty =
    form.value.sofDocumentUpload.length === 0 || form.value.tnc === false;
  return anyErrors || anyFieldsEmpty;
});

watch(
  () => form.value.tnc,
  (tnc) => {
    const tncErrors = validateCheckboxField("source of funds declaration", tnc);
    form.value.tncErrors = tncErrors;
  }
);

const handleFormSubmit = async () => {
  const custId = await fetchCustomerId();
  const formData = new FormData();

  formData.set("documentReference", custId);
  form.value.sofDocumentUpload.forEach((doc) =>
    formData.append("documentFiles[]", doc)
  );

  formData.set("documentType", "sof_document");

  globalStore.setIsFormLoading(true);
  postRequest("documents", formData, () => {
    identityQueueStore.updateToNextRoute();
    identityQueueStore.fetchNextIdentityRoute(route).then((nextRoute) => {
      router.push(nextRoute);
    });
  });
};
const bodyCopy = computed(() =>
  t("PageSubTitle.paragraph.sofDocumentUploadInstructions2").split("','")
);
</script>
<style lang="scss" scoped>
.form {
  .bodycopy {
    margin-bottom: 1rem;
    @include breakpoint($min-m) {
      margin-bottom: 1.5rem;
    }
  }
  &__document-instructions {
    list-style: unset;
    padding-inline: 1.5rem;

    li {
      font-size: $font-body;
      line-height: 1.5;
    }
  }
}
</style>
